import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { Fingerprint, Trash2, Plus, Loader, Pencil } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import { SettingsSection } from '../../../components/ui/settings-section';
import {
  browserSupportsWebAuthn,
  registerPasskey,
  listPasskeys,
  deletePasskey,
  renamePasskey,
  type PasskeyCredentialInfo,
} from '../../../services/passkeyService';
import {
  trackPasskeyRegistered,
  trackPasskeyDeleted,
  trackPasskeyRenamed,
} from '../../../middleware/posthogMiddleware';

const PasskeyManager: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [passkeys, setPasskeys] = useState<PasskeyCredentialInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newPasskeyName, setNewPasskeyName] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const supported = browserSupportsWebAuthn();

  const fetchPasskeys = useCallback(async () => {
    try {
      const data = await listPasskeys();
      setPasskeys(data);
    } catch {
      toast.error(t('settings.passkeys.loadFailed', 'Failed to load passkeys'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (supported) fetchPasskeys();
    else setIsLoading(false);
  }, [supported, fetchPasskeys]);

  const handleRegister = async () => {
    setIsRegistering(true);
    try {
      await registerPasskey(newPasskeyName || 'Passkey');
      dispatch(trackPasskeyRegistered({ name: newPasskeyName || 'Passkey' }));
      toast.success(
        t('settings.passkeys.registered', 'Passkey registered successfully'),
      );
      setNewPasskeyName('');
      await fetchPasskeys();
    } catch (error: any) {
      if (error.name !== 'NotAllowedError') {
        toast.error(
          error.message ||
            t('settings.passkeys.registerFailed', 'Failed to register passkey'),
        );
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deletePasskey(id);
      dispatch(trackPasskeyDeleted({ passkeyId: id }));
      toast.success(t('settings.passkeys.deleted', 'Passkey deleted'));
      setPasskeys((prev) => prev.filter((p) => p._id !== id));
    } catch {
      toast.error(
        t('settings.passkeys.deleteFailed', 'Failed to delete passkey'),
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleRename = async (id: string) => {
    try {
      await renamePasskey(id, editName);
      dispatch(trackPasskeyRenamed({ passkeyId: id, newName: editName }));
      setPasskeys((prev) =>
        prev.map((p) => (p._id === id ? { ...p, friendlyName: editName } : p)),
      );
      setEditingId(null);
      toast.success(t('settings.passkeys.renamed', 'Passkey renamed'));
    } catch {
      toast.error(
        t('settings.passkeys.renameFailed', 'Failed to rename passkey'),
      );
    }
  };

  if (!supported) return null;

  return (
    <>
      <SettingsSection
        label={t('settings.passkeys.title', 'Passkeys')}
        description={t(
          'settings.passkeys.description',
          'Use biometrics or device PIN to sign in without a password',
        )}
      >
        <Button
          variant="minimal"
          onClick={() => setIsDialogOpen(true)}
          className="text-black hover:text-black"
        >
          {t('settings.passkeys.manage', 'Manage')}
        </Button>
      </SettingsSection>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Fingerprint className="h-5 w-5" />
              {t('settings.passkeys.title', 'Passkeys')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Register new passkey */}
            <div className="flex gap-2">
              <Input
                value={newPasskeyName}
                onChange={(e) => setNewPasskeyName(e.target.value)}
                placeholder={t(
                  'settings.passkeys.namePlaceholder',
                  'e.g. MacBook, iPhone',
                )}
                className="flex-1 h-9"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRegister();
                }}
              />
              <Button
                size="sm"
                onClick={handleRegister}
                disabled={isRegistering}
                className="h-9"
              >
                {isRegistering ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-1" />
                )}
                {t('settings.passkeys.add', 'Add')}
              </Button>
            </div>

            {/* Passkey list */}
            {isLoading && (
              <div className="flex justify-center py-8">
                <Loader className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
            {!isLoading && passkeys.length === 0 && (
              <div className="text-center py-8 text-sm text-muted-foreground">
                {t(
                  'settings.passkeys.noPasskeys',
                  'No passkeys registered yet',
                )}
              </div>
            )}
            {!isLoading && passkeys.length > 0 && (
              <div className="border rounded-md max-h-[300px] overflow-y-auto">
                <div className="divide-y">
                  {passkeys.map((pk) => (
                    <div
                      key={pk._id}
                      className="flex items-center justify-between px-3 py-2 hover:bg-muted/50 group"
                    >
                      <div className="flex-1 min-w-0">
                        {editingId === pk._id ? (
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="h-7 text-sm"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRename(pk._id);
                              if (e.key === 'Escape') setEditingId(null);
                            }}
                            autoFocus
                          />
                        ) : (
                          <>
                            <p className="text-sm font-medium truncate">
                              {pk.friendlyName}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {t('settings.passkeys.created', 'Created')}:{' '}
                              {new Date(pk.createdAt).toLocaleDateString()}
                              {pk.lastUsedAt &&
                                ` | ${t('settings.passkeys.lastUsed', 'Last used')}: ${new Date(pk.lastUsedAt).toLocaleDateString()}`}
                              {pk.backedUp &&
                                ` | ${t('settings.passkeys.synced', 'Synced')}`}
                            </p>
                          </>
                        )}
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingId(pk._id);
                            setEditName(pk.friendlyName);
                          }}
                          className="h-7 w-7 p-0"
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(pk._id)}
                          disabled={deletingId === pk._id}
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        >
                          {deletingId === pk._id ? (
                            <Loader className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setIsDialogOpen(false)}>
              {t('common.done', 'Done')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PasskeyManager;
